/**
 * History page navigation (pages/mb-quart-legacy)
 *
 * One timeline that does two jobs:
 *   - Desktop: sits under the hero as a horizontal dot-timeline TOC, then
 *     pins below the sticky header — following its LIVE bottom position, so
 *     it rises to hug the top when the on-scroll-up header slides away.
 *   - Mobile: the same element becomes a fixed vertical rail on the right
 *     edge of the page (dots stacked, marker gliding downward).
 *
 * Two independent signals drive the widget:
 *   1. CURRENT ERA (the glowing dot) — the era whose chapter section fills
 *      the MOST of the visible viewport. Each era's section runs from its
 *      heading to the next era's heading (the last runs to the page end).
 *      This is a pure function of scroll position and chapter geometry, so
 *      the header's hide/show never affects which era is highlighted. *  2. MARKER / PROGRESS — a continuous scroll gauge. The probe is a FIXED
 *      viewport line, so the marker interpolates the era block starts'
 *      absolute page positions as they cross it — a pure function of scroll,
 *      with no spring, no transitions, and no dependence on the header.
 *
 * No template edits required: it locates each era's chapter section by
 * matching the heading text rendered by the rich-text section.
 */
(() => {
  // Match strings are the distinctive year prefixes of the chapter headings.
  // Keep them loose (year only) so subtitle edits in the admin don't drop an
  // era; '2008-Today' stays full to distinguish it from the 2008 buyout chapter.
  const ERAS = [
    { key: '60s', label: '60s', match: '1962' },
    { key: '70s', label: '70s', match: '1974' },
    { key: '80s', label: '80s', match: '1983' },
    { key: '90s', label: '90s', match: '1990s' },
    { key: '00s', label: '00s', match: '2001' },
    { key: 'Today', label: 'Today', match: '2008-Today' },
  ];

  const main = document.querySelector('#MainContent');
  if (!main) return;

  // Find each era's chapter section via its rich-text heading text.
  const eras = [];
  ERAS.forEach((era) => {
    const heading = Array.from(main.querySelectorAll('.rich-text__heading')).find((el) =>
      el.textContent.trim().startsWith(era.match)
    );
    if (!heading) return;
    // Real anchor targets so the hash links work natively too.
    heading.id = era.key;
    eras.push({ ...era, heading });
  });
  if (eras.length < 2) return;

  // ---- Build the timeline and insert it after the hero ----
  const timeline = document.createElement('nav');
  timeline.className = 'history-timeline';
  timeline.setAttribute('aria-label', 'Jump to an era');
  timeline.innerHTML = `
    <div class="history-timeline__inner">
      <p class="history-timeline__label">60 years of German engineering</p>
      <div class="history-timeline__track">
        <span class="history-timeline__progress" aria-hidden="true"></span>
        <span class="history-timeline__marker" aria-hidden="true"></span>
        ${eras
          .map(
            (era) => `
          <a class="history-timeline__item" href="#${era.key}" data-era="${era.key}">
            <span class="history-timeline__dot" aria-hidden="true"></span>
            <span class="history-timeline__year">${era.label}</span>
          </a>`
          )
          .join('')}
      </div>
    </div>`;

  const hero = main.querySelector('.shopify-section');
  if (!hero) return;
  hero.after(timeline);

  const progress = timeline.querySelector('.history-timeline__progress');
  const marker = timeline.querySelector('.history-timeline__marker');
  const items = Array.from(timeline.querySelectorAll('.history-timeline__item'));
  const links = Array.from(document.querySelectorAll('[data-era]'));

  // ---- Layout mode: horizontal bar (desktop) vs side rail (mobile) ----
  const isVertical = () => window.matchMedia('(max-width: 749px)').matches;

  // ---- Sticky anchor: the RAIL follows the header's LIVE bottom ----
  // When the on-scroll-up header slides away, the rail rises to hug the top
  // instead of floating below an empty band. This affects the rail's visual
  // position ONLY — the marker gauge and click landing are anchored to a
  // fixed viewport probe (below), never to the header.
  const header = document.querySelector('.section-header') || null;
  let railTop = 0;
  // Fixed viewport probe for the marker and click landing: the era block
  // start (its heading) crossing this line puts the marker exactly on that
  // era's dot. It's a CONSTANT between resizes, so the gauge is a pure
  // function of absolute page position — the header's hide/show cannot move it.
  let refPad = 64;
  const refreshRem = () => {
    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 10;
    if (isVertical()) {
      refPad = Math.round(rem * 1.6);
      return;
    }

    // Keep the fixed marker/click line below the maximum desktop obstruction:
    // the full header, the constant-flow timeline, and a small breathing gap.
    // This is measured on resize, not during scrolling, so header animation
    // cannot move the marker or invalidate a smooth-scroll destination.
    const headerHeight = header ? header.offsetHeight : 0;
    const timelineHeight = timeline.getBoundingClientRect().height;
    refPad = Math.ceil(headerHeight + timelineHeight + rem * 1.6);
  };
  const measureOffset = () => {
    const headerRect = header ? header.getBoundingClientRect() : null;
    railTop = headerRect ? Math.max(0, Math.round(headerRect.bottom)) : 0;
    // Desktop pins below the header; the mobile rail is fixed via CSS.
    const vertical = isVertical();
    timeline.style.top = vertical ? '' : railTop + 'px';
    // Don't let the desktop pinning state leak onto the side rail.
    if (vertical) timeline.classList.remove('is-stuck');
  };

  // ---- Current-era highlight ----
  let activeKey = null;
  const setActive = (key) => {
    if (key === activeKey) return;
    activeKey = key;
    links.forEach((link) => link.classList.toggle('is-active', link.dataset.era === key));
  };

  // Map the fixed document-space probe to the timeline with a monotone cubic
  // spline. Every era point remains exact, while the slope also changes
  // continuously at each point instead of resetting at every chapter.
  const interpolateMarker = (value, sourcePoints, targetPoints) => {
    const last = sourcePoints.length - 1;
    if (last < 1 || value <= sourcePoints[0]) return targetPoints[0];
    if (value >= sourcePoints[last]) return targetPoints[last];

    let segment = 0;
    while (segment < last - 1 && value > sourcePoints[segment + 1]) segment += 1;

    const spans = sourcePoints.slice(0, -1).map((point, index) => sourcePoints[index + 1] - point);
    const slopes = spans.map((span, index) =>
      span > 0 ? (targetPoints[index + 1] - targetPoints[index]) / span : 0
    );
    const tangents = new Array(sourcePoints.length).fill(0);

    if (slopes.length === 1) {
      tangents[0] = slopes[0];
      tangents[1] = slopes[0];
    } else {
      // Fritsch-Carlson/PCHIP-style tangents keep each Hermite segment
      // monotone, even when chapter lengths differ sharply.
      const limitEndpoint = (leftSpan, rightSpan, leftSlope, rightSlope) => {
        let tangent = ((2 * leftSpan + rightSpan) * leftSlope - leftSpan * rightSlope) / (leftSpan + rightSpan);
        if (tangent * leftSlope <= 0) tangent = 0;
        else if (Math.abs(tangent) > 3 * Math.abs(leftSlope)) tangent = 3 * leftSlope;
        return tangent;
      };

      tangents[0] = limitEndpoint(spans[0], spans[1], slopes[0], slopes[1]);
      tangents[last] = limitEndpoint(spans[last - 1], spans[last - 2], slopes[last - 1], slopes[last - 2]);

      for (let index = 1; index < last; index += 1) {
        const previous = slopes[index - 1];
        const next = slopes[index];
        if (previous * next <= 0 || spans[index - 1] <= 0 || spans[index] <= 0) continue;
        const weightPrevious = 2 * spans[index] + spans[index - 1];
        const weightNext = spans[index] + 2 * spans[index - 1];
        tangents[index] =
          (weightPrevious + weightNext) /
          (weightPrevious / previous + weightNext / next);
      }
    }

    const h = spans[segment];
    if (h <= 0) return targetPoints[segment];
    const t = (value - sourcePoints[segment]) / h;
    const t2 = t * t;
    const t3 = t2 * t;
    const h00 = 2 * t3 - 3 * t2 + 1;
    const h10 = t3 - 2 * t2 + t;
    const h01 = -2 * t3 + 3 * t2;
    const h11 = t3 - t2;

    return (
      h00 * targetPoints[segment] +
      h10 * h * tangents[segment] +
      h01 * targetPoints[segment + 1] +
      h11 * h * tangents[segment + 1]
    );
  };

  // ---- Frame-by-frame tracking ----
  // Chapter bounds are read LIVE every frame as absolute document offsets —
  // they are scroll-invariant, so this is cheap and always reflects the true
  // layout, even if a lazy-loaded image or reveal animation shifts the
  // headings after load.
  const onScroll = () => {
    measureOffset();
    const vertical = isVertical();
    if (!vertical) {
      timeline.classList.toggle('is-stuck', timeline.getBoundingClientRect().top <= railTop + 2);
    }

    // The probe is a fixed viewport line (scrollY + refPad), so the marker
    // is a pure function of absolute page position — the era block start
    // crossing the line is exactly what the gauge reflects, regardless of
    // what the header is doing.
    const readLine = window.scrollY + refPad;
    const eraTops = eras.map((era) => era.heading.getBoundingClientRect().top + window.scrollY);
    const viewTop = window.scrollY;
    const viewBottom = viewTop + window.innerHeight;
    const center = viewTop + window.innerHeight / 2;
    // Each era's section ends where the next era's heading begins (last one
    // runs to the end of the page).
    const chapterEnds = [...eraTops.slice(1), document.documentElement.scrollHeight];

    // ---- 1. Current era = the chapter section with the most visible area ----
    let current = 0;
    let maxArea = 0;
    eras.forEach((era, i) => {
      const area = Math.max(0, Math.min(chapterEnds[i], viewBottom) - Math.max(eraTops[i], viewTop));
      if (area > maxArea) {
        maxArea = area;
        current = i;
      }
    });
    // Tie-break: prefer the chapter containing the viewport center.
    eras.forEach((era, i) => {
      const area = Math.max(0, Math.min(chapterEnds[i], viewBottom) - Math.max(eraTops[i], viewTop));
      if (area === maxArea && center >= eraTops[i] && center < chapterEnds[i]) {
        current = i;
      }
    });

    // ---- 2. Marker gauge: smoothly interpolate all era stops ----
    // Keep the era headings and dots as exact interpolation points, but use a
    // monotone spline across the complete range. This prevents the velocity
    // from resetting when a long jump crosses an intermediate era.
    const dotPositions = items.map((item) =>
      vertical ? item.offsetTop + item.offsetHeight / 2 : item.offsetLeft + item.offsetWidth / 2
    );
    const markerPos = interpolateMarker(readLine, eraTops, dotPositions);

    // The marker and progress glide EXACTLY with the scroll (no transition,
    // no spring). Clear the other axis's inline props so orientation changes
    // (desktop <-> mobile rail) don't leave stale values behind.
    marker.style.transition = 'none';
    progress.style.transition = 'none';
    if (vertical) {
      marker.style.left = '';
      progress.style.left = '';
      progress.style.width = '';
      marker.style.top = markerPos + 'px';
      progress.style.top = dotPositions[0] + 'px';
      progress.style.height = Math.max(0, markerPos - dotPositions[0]) + 'px';
    } else {
      marker.style.top = '';
      progress.style.top = '';
      progress.style.height = '';
      marker.style.left = markerPos + 'px';
      progress.style.left = dotPositions[0] + 'px';
      progress.style.width = Math.max(0, markerPos - dotPositions[0]) + 'px';
    }

    setActive(eras[current].key);
  };

  // ---- Smooth scroll to a chapter ----
  // Land the heading just below the rail, on the SAME fixed probe line the
  // marker uses — the era's block start crossing the probe puts the marker
  // exactly on that era's dot. Fixed offset, no header-state guessing: the
  // landing is identical in every scroll direction and header state.
  const smoothScrollTo = (heading) => {
    const targetY = heading.getBoundingClientRect().top + window.scrollY;
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    window.scrollTo({ top: Math.max(0, targetY - refPad), behavior });
  };

  links.forEach((link) => {
    link.addEventListener('click', (event) => {
      event.preventDefault();
      const era = eras.find((e) => e.key === link.dataset.era);
      if (!era) return;
      smoothScrollTo(era.heading);
    });
  });

  // ---- Runners ----
  let ticking = false;
  const requestTick = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(() => {
      onScroll();
      ticking = false;
    });
  };
  window.addEventListener('scroll', requestTick, { passive: true });

  // ---- Re-pin after the header finishes moving ----
  // The header's hide/reveal slides its `top` over 150ms (`.section-header.animate`).
  // That transition can outlive the last scroll event — especially during rapid
  // back-and-forth scrolling — so the timeline would stay pinned at a mid-
  // transition offset, leaving a gap, until the next scroll nudges a re-measure.
  const onPinSettle = () => {
    measureOffset();
    requestTick();
  };
  if (header) {
    header.addEventListener('transitionend', (event) => {
      if (event.target === header && event.propertyName === 'top') onPinSettle();
    });
  }
  window.addEventListener('scrollend', onPinSettle);

  const onResize = () => {
    refreshRem();
    measureOffset();
    requestTick();
  };
  window.addEventListener('resize', onResize, { passive: true });

  // Re-pin on load in case fonts/layout shifted the header or track height.
  window.addEventListener('load', () => {
    measureOffset();
    requestTick();
  });

  refreshRem();
  measureOffset();
  onScroll();
})();
